import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import userService from "../../services/userService";
import profileService from "../../services/profileService";
import { queryKeys } from "./queryKeys";

// Fetch all users
export const useUsers = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: queryKeys.users.list(filters),
    queryFn: () => userService.getUsers(filters),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

// Fetch single user
export const useUser = (id) => {
  return useQuery({
    queryKey: queryKeys.users.detail(id),
    queryFn: () => userService.getUserById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

// Fetch students
export const useStudents = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.users.students(filters),
    queryFn: () => userService.getStudents(filters),
    staleTime: 5 * 60 * 1000,
  });
};

// Fetch teachers
export const useTeachers = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.users.teachers(filters),
    queryFn: () => userService.getTeachers(filters),
    staleTime: 5 * 60 * 1000,
  });
};

// Fetch admins
export const useAdmins = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.users.admins(filters),
    queryFn: () => userService.getUsers({ ...filters, role: "admin" }),
    staleTime: 5 * 60 * 1000,
  });
};

// Fetch students by class
export const useStudentsByClass = (classId) => {
  return useQuery({
    queryKey: [...queryKeys.users.students(), "class", classId],
    queryFn: () => userService.getStudentsByClass(classId),
    enabled: !!classId,
    staleTime: 5 * 60 * 1000,
  });
};

// Fetch teachers by department
export const useTeachersByDepartment = (departmentId) => {
  return useQuery({
    queryKey: [...queryKeys.users.teachers(), "department", departmentId],
    queryFn: () => userService.getTeachersByDepartment(departmentId),
    enabled: !!departmentId,
    staleTime: 5 * 60 * 1000,
  });
};

// Create user
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData) => userService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.lists(),
      });
    },
  });
};

// Update user
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userData }) => userService.updateUser(id, userData),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.lists(),
      });
    },
  });
};

// Delete user
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => userService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.lists(),
      });
    },
  });
};

// Assign role to user
export const useAssignRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, roleId }) => userService.assignRole(userId, roleId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.detail(variables.userId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.lists(),
      });
    },
  });
};

// Update profile
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profileData) => profileService.updateProfile(profileData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.current(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.lists(),
      });
    },
  });
};

// Request contact update
export const useRequestContactUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contactData) => userService.requestContactUpdate(contactData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.contactRequests(),
      });
    },
  });
};

// Approve contact update
export const useApproveContactUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId) => userService.approveContactUpdate(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.contactRequests(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.lists(),
      });
    },
  });
};

// Reject contact update
export const useRejectContactUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, reason }) => userService.rejectContactUpdate(requestId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.contactRequests(),
      });
    },
  });
};

// Import users
export const useImportUsers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fileData, type }) => userService.importUsers(fileData, type),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.lists(),
      });
    },
  });
};

// Export users
export const useExportUsers = () => {
  return useMutation({
    mutationFn: (params) => userService.exportUsers(params),
  });
};
